import type { AxiosRequestConfig } from 'axios';
import { apiClient, postForm, type FormFields } from './client';

export type FieldErrors = Record<string, string[]>;

interface Envelope {
  success?: number;
  errors?: unknown;
}

const COMMON_ERROR = 'Ошибка сервера';

export class ApiError extends Error {
  readonly errors: FieldErrors;

  constructor(errors: FieldErrors) {
    super(Object.values(errors).flat().join('; '));
    this.errors = errors;
  }
}

const normalizeErrors = (raw: unknown): FieldErrors => {
  if (!raw || typeof raw !== 'object') return { common: [COMMON_ERROR] };
  const result: FieldErrors = {};
  Object.entries(raw as Record<string, unknown>).forEach(([field, value]) => {
    result[field] = Array.isArray(value) ? value.map(String) : [String(value)];
  });
  return Object.keys(result).length ? result : { common: [COMMON_ERROR] };
};

const unwrap = <T extends Envelope>(data: T | null): T => {
  if (data && typeof data === 'object' && data.success === 1) return data;
  throw new ApiError(normalizeErrors(data?.errors));
};

/** POST form, resolve on `success === 1`, otherwise reject with field-level `ApiError` (also for 4xx bodies). */
export async function submitForm<T extends Envelope = Envelope>(
  url: string,
  fields: FormFields = {},
  config: AxiosRequestConfig = {},
): Promise<T> {
  return unwrap(await postForm<T | null>(url, fields, { ...config, validateStatus: () => true }));
}

/** Like `submitForm` for a prebuilt body, e.g. when empty strings must reach the server. */
export async function submitFormData<T extends Envelope = Envelope>(url: string, body: FormData): Promise<T> {
  const { data } = await apiClient.post<T | null>(url, body, { validateStatus: () => true });
  return unwrap(data);
}

export const toFieldErrors = (error: unknown): FieldErrors =>
  error instanceof ApiError ? error.errors : { common: [COMMON_ERROR] };
